<template>
  <div>
    <page-help
        page="stops" title="help_stops_title" body="help_stops_body"
        :points="['help_stops_p1', 'help_stops_p2']" next="help_stops_next"
    ></page-help>
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        mdi-bus-stop
      </v-icon>
        <span class="px-2">{{ $t('stops') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="createStop"></create-button>
        <activation-tool-tip model="stops"></activation-tool-tip>
      </v-card-title>
      <v-data-table
        v-model="selected"
        show-select
        :options.sync="tableOptions"
:footer-props="footerProps"
        :no-data-text="$t('no_stops_yet')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="translatedHeaders"
        :items="stops"
        :search="debouncedSearch"
      >
        <template v-slot:top>
          <bulk-action-bar
            :count="selected.length"
            :running="bulkRunning"
            :done="bulkDone"
            :total="bulkTotal"
            :progress="bulkProgress"
            @clear="clearSelection"
          >
            <v-btn text small @click="bulkDelete">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
          </bulk-action-bar>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>

        <!-- who waits here in the morning: a stop with nobody at it is one to
             drop from the route, and there was no way to see that -->
        <template v-slot:item.students="{ item }">
          <v-chip
            small
            dark
            :color="studentCount(item) ? 'primary' : 'warning'"
            @click.stop="showStudents(item)"
          >
            {{ studentCount(item) }}
          </v-chip>
        </template>
        <!-- the routes themselves, not the number of them: "3" told nobody
             which ones, so seeing that meant opening the dialog every time -->
        <template v-slot:item.routes="{ item }">
          <span v-if="item.routes.length">
            <v-chip
              v-for="route in item.routes.slice(0, 3)"
              :key="route.id"
              class="ma-1"
              x-small
              dark
              :color="route.is_morning ? 'primary' : 'secondary'"
              @click="viewRoute(route)"
            >
              {{ route.name }}
            </v-chip>
            <a
              v-if="item.routes.length > 3"
              class="mx-1"
              @click.stop="displayRoutes(stops.indexOf(item))"
            >
              {{ $t('and_n_more', { count: item.routes.length - 3 }) }}
            </a>
          </span>
          <span v-else>{{ $t('no_routes') }}</span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mx-2" @click="viewStop(item)">
            mdi-eye
          </v-icon>
          <v-icon small class="mx-2" @click="editStop(item)">
            mdi-pencil
          </v-icon>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-icon
                  class="mx-2"
                  small
                  :disabled="stopIsInUse(item)"
                  @click="deleteStop(item)"
                >
                  mdi-delete
                </v-icon>
              </span>
            </template>
            <span v-if="stopIsInUse(item)">
              {{ $t('cannot_delete_stop_message', { name: item.name }) }}
            </span>
            <span v-else>{{ $t('delete_stop') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-if="stops[selectedStop]" v-model="dialog" max-width="290">
      <v-card>
        <v-card-title class="text-h5">
          {{ stops[selectedStop].name }}
        </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-subheader>{{ $t('routes') }}</v-subheader>
            <v-list-item-group>
              <v-list-item
                v-for="(item, i) in stops[selectedStop].routes"
                :key="i"
              >
                <v-icon small class="mx-2">
                  mdi-eye
                </v-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="item.name" @click="viewRoute(item)"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="green darken-1" text @click="dialog = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <undo-bar
      :pending="pendingUndo"
      :seconds-left="undoSecondsLeft"
      @undo="undoPendingRemove"
    ></undo-bar>
    <bulk-result-dialog
      v-model="bulkResultDialog"
      :title="bulkResultTitle"
      :failures="bulkFailures"
    ></bulk-result-dialog>
    <students-on-dialog
      v-model="studentsDialog"
      scope="stop"
      :subject="studentsSubject"
    ></students-on-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import PageHelp from "@/components/PageHelp";
import CreateButton from "@/components/CreateButton";
import UndoBar from "@/components/UndoBar";
import undoable from "@/mixins/undoable";

import tableState from "@/mixins/tableState";
import bulkActions from "@/mixins/bulkActions";
import BulkActionBar from "@/components/BulkActionBar";
import BulkResultDialog from "@/components/BulkResultDialog";
import StudentsOnDialog from "@/components/StudentsOnDialog";

export default {
  mixins: [undoable, tableState, bulkActions],
  components: {
    PageHelp,
    BulkActionBar,
    BulkResultDialog,
    StudentsOnDialog,
    ActivationToolTip,
    CreateButton,
    UndoBar,
  },
  data() {
    return {
      stops: [],
      studentCounts: {},
      studentsDialog: false,
      studentsSubject: null,
      dialog: false,
      isLoading: false,
      selectedStop: null,
      tableStateKey: "stops",
      headers: [
        { text: "ID", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Address", value: "address", width: 300 },
        { text: "students", value: "students", sortable: false },
        { text: "Routes", value: "routes" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => {
        return {
          ...header,
          text: this.$t(header.text.toLowerCase().replace(' ', '_'))
        };
      });
    },
  },
  mounted() {
    this.loadStops();
    this.loadStudentCounts();
  },
  methods: {
    //one request for the whole table rather than one per row
    loadStudentCounts() {
      axios
        .get("/school-students/assignments")
        .then((response) => {
          this.studentCounts = (response.data && response.data.stops) || {};
        })
        .catch(() => {
          //the rest of the table is still worth showing
        });
    },
    studentCount(stop) {
      return this.studentCounts[stop.id] || 0;
    },
    showStudents(stop) {
      this.studentsSubject = stop;
      this.studentsDialog = true;
    },
    bulkDelete() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete", { count: this.selected.length }),
        label: (stop) => stop.name,
        skip: (stop) => (this.stopIsInUse(stop) ? this.$t("stop_on_route") : null),
        request: (stop) => axios.delete(`/stops/${stop.id}`),
        onSuccess: (stop) => this.removeFromList(this.stops, stop),
      });
    },
    displayRoutes(index) {
      this.selectedStop = index;
      this.dialog = true;
    },
    viewRoute(route) {
      this.$router.push({
        name: "view-route",
        params: { route_id: route.id},
      });
    },
    loadStops() {
      this.isLoading = true;
      this.stops = [];
      axios
        .get(`/stops/all`)
        .then((response) => {
          this.stops = response.data;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_stops"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    createStop() {
      this.$router.push({
        name: "create-stop",
      });
    },
    viewStop(stop)
    {
      this.$router.push({
        name: "view-stop",
        params: {
          stop_id: stop.id,
        },
      });
    },
    editStop(stop)
    {
      this.$router.push({
        name: "edit-stop",
        params: {
          stop_id: stop.id,
        },
      });
    },
    //a stop on a route cannot be removed; the row says so up front instead of
    //letting the admin click and then explaining
    //a stop is in use when a route calls at it, and equally when a child is
    //picked up or dropped off there - deleting one of those left the child
    //with a pickup point that no longer exists
    stopIsInUse(stop) {
      if (stop.routes && stop.routes.length > 0) return true;
      return this.studentCount(stop) > 0;
    },
    deleteStop(stop) {
      if (this.stopIsInUse(stop)) {
        return;
      }

      this.undoableRemove({
        item: stop,
        list: this.stops,
        label: stop.name,
        request: () => axios.delete(`/stops/${stop.id}`),
        onDone: () => this.$notifySuccess(this.$t("stop_deleted")),
        onError: (error) => this.$notifyError(error, this.$t("error_deleting_stops")),
      });
    },
  },
};
</script>

<style lang="scss">
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover{
  cursor: pointer;
  background: rgba($primary-shade--light, 0.15) !important;
}
</style>
